import type { JSX } from 'react';
import { addToast } from '@heroui/react';

import { useRemoveMember } from '~/hooks/useMembers';
import { useTenant } from '~/hooks/useTenant';
import { getDisplayName } from '~/utils/member';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

import type { OrganizationMember } from '~/types';

interface RemoveMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: OrganizationMember;
  orgName: string;
}

export function RemoveMemberConfirmation(props: RemoveMemberModalProps): JSX.Element {
  const { isOpen, member, onClose, orgName } = props;
  const { currentOrg } = useTenant();
  const { isPending, mutate } = useRemoveMember();

  const handleRemove = (): void => {
    if (!currentOrg) {
      return;
    }

    mutate(
      {
        orgId: currentOrg.id,
        userId: member.user_id,
      },
      {
        onSuccess: () => {
          addToast({
            description: `${getDisplayName(member)} has been removed from ${orgName}`,
            color: 'success',
          });
          onClose();
        },
        onError: (error: Error) => {
          let description = error.message || 'Failed to remove member.';

          if (error.message.includes('403')) {
            description = 'You do not have permission to remove members.';
          }

          addToast({
            description,
            color: 'danger',
          });
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <ConfirmationDialog
      content={
        <>
          <p>
            Are you sure you want to remove{' '}
            <span className="font-semibold">{getDisplayName(member)}</span> from{' '}
            <span className="font-semibold">{orgName}</span>?
          </p>
          <p className="mt-1 text-sm text-muted">
            This action cannot be undone. The user will lose access to the organization immediately.
          </p>
        </>
      }
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      onConfirm={handleRemove}
      title="Remove Member"
    />
  );
}
